import { Link } from "react-router-dom";
import { Navbar } from "../../components/Navbar";
import { Footer } from "../../components/Footer";

/**
 * Intakall for Zoom: how to add, use and remove the app. Zoom Marketplace review requires this page
 * (Documentation URL on the listing). Keep it in step with the Zoom panel (platform repo,
 * dashboard/src/pages/ZoomPanel.tsx) and with what the server deletes when the app is removed.
 */
export default function ZoomGuide() {
  return (
    <div className="min-h-screen bg-white font-body text-slate-800">
      <Navbar />
      <main>
        <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed">
          <header className="mb-10 border-b border-slate-200 pb-6">
            <h1 className="text-4xl font-semibold text-slate-900">Intakall for Zoom</h1>
            <p className="mt-2 text-sm text-slate-500">Last updated: 2026-10-17</p>
            <p className="mt-6">
              Intakall brings your team's work into your Zoom meetings, live. One person presses Start recording, and the
              ticket, pull request or thread you mention appears in the Intakall panel for everyone on your team
              in the call. When the meeting ends, the transcript, summary and action items are ready.
            </p>
          </header>

          <Section n="1" title="Before you start">
            <ul className="ml-6 list-disc space-y-1">
              <li>A Zoom account and the Zoom desktop app (Zoom Apps run in the Zoom client).</li>
              <li>A Google account to sign in to Intakall.</li>
              <li>
                Your Zoom account admin may need to allow Zoom Apps or approve Intakall before you can add it.
              </li>
            </ul>
          </Section>

          <Section n="2" title="Adding Intakall">
            <ol className="ml-6 list-decimal space-y-2">
              <li>Open Intakall in the Zoom App Marketplace and click <strong>Add</strong>.</li>
              <li>Review the permissions Intakall asks for and click <strong>Allow</strong>.</li>
              <li>
                Zoom sends you to Intakall. Sign in with Google, then create your team or join the one your
                teammates already use.
              </li>
              <li>
                Connect the work tools your team uses (for example your issue tracker, code host or chat). You
                do this once, on the Integrations page.
              </li>
            </ol>
            <p>
              You can also add Intakall from inside a meeting: open <strong>Apps</strong> in the Zoom meeting
              toolbar, find Intakall and follow the same steps.
            </p>
          </Section>

          <Section n="3" title="Using Intakall in a meeting">
            <ol className="ml-6 list-decimal space-y-2">
              <li>
                In a Zoom meeting, click <strong>Apps</strong> in the toolbar and open <strong>Intakall</strong>.
                The panel opens on the right. The first time, it may ask you to <strong>Connect Zoom</strong>:
                click it and approve in your browser, and the panel carries on by itself.
              </li>
              <li>
                Choose who the notes are for: <strong>Personal</strong> (only you) or <strong>Team</strong>
                {" "}(teammates in the call see live cards and keep access to the notes).
              </li>
              <li>
                Press <strong>Start recording</strong>. Let people know you are recording and taking notes. Zoom also shows
                participants that an app is accessing the meeting.
              </li>
              <li>
                Talk as normal. When someone mentions a ticket, a pull request or a thread, a card with it appears
                in the panel.
              </li>
              <li>
                To raise a ticket, say what you need or type it in the panel. Intakall drafts it; click
                {" "}<strong>Create</strong> to file it or <strong>Dismiss</strong> to drop it.
              </li>
              <li>
                Press <strong>Stop recording</strong> at any time. Capture also ends when the meeting ends.
              </li>
              <li>
                Open your meeting at <a href="https://app.intakall.io" className="text-indigo-600 hover:underline">app.intakall.io</a>
                {" "}for the transcript, summary and action items.
              </li>
            </ol>
            <p>
              Nothing is captured until someone presses Start recording. Intakall receives the meeting audio from Zoom only
              while capture is running, and no bot joins your call.
            </p>
          </Section>

          <Section n="4" title="Removing Intakall">
            <ol className="ml-6 list-decimal space-y-2">
              <li>
                Sign in at <a href="https://marketplace.zoom.us" className="text-indigo-600 hover:underline">marketplace.zoom.us</a>
                {" "}and click <strong>Manage</strong>, then <strong>Added Apps</strong>.
              </li>
              <li>Find Intakall and click <strong>Remove</strong>.</li>
            </ol>
            <p>
              When you remove the app, Zoom tells Intakall and we delete the Zoom connection we hold for you,
              including your Zoom access tokens. You can also disconnect Zoom from the Integrations page in
              Intakall.
            </p>
            <p>
              Removing the Zoom app does not delete your past meeting notes, because they belong to your Intakall
              account and may be shared with your team. To delete your notes or your whole account, delete them in
              Intakall or email{" "}
              <a href="mailto:privacy@intakall.io" className="text-indigo-600 hover:underline">privacy@intakall.io</a>.
              See our <Link to="/privacy" className="text-indigo-600 hover:underline">Privacy Policy</Link> for how
              long we keep data.
            </p>
          </Section>

          <Section n="5" title="Help">
            <p>
              Something not working? See <Link to="/support" className="text-indigo-600 hover:underline">Support</Link>
              {" "}or email{" "}
              <a href="mailto:hello@intakall.io" className="text-indigo-600 hover:underline">hello@intakall.io</a>.
            </p>
          </Section>
        </article>
      </main>
      <Footer />
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-semibold text-slate-900">
        <span className="mr-3 text-slate-400">{n}.</span>
        {title}
      </h2>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  );
}
